import React, { useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Button, Modal, TextInput, Textarea,
  NumberInput, Select, Switch, Loader, ScrollArea, ActionIcon, Tooltip, Alert,
} from '@mantine/core';
import { IconPlus, IconEdit, IconShieldLock, IconAlertCircle } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import useAuthenticatedPut from '../../../hooks/useAuthenticatedPut';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

const HARD_TYPES = ['', 'credit-hold', 'margin-floor', 'do-not-quote'];

// Field + column schema per guidance tier. Keeps the three editors in one component.
const SCHEMAS = {
  house: {
    label: 'House rule',
    endpoint: 'house',
    blurb: 'Global rules applied to every opportunity. Typed hard rules (credit-hold, margin-floor, do-not-quote) are enforced in code — the AI cannot soften them.',
    fields: [
      { name: 'rule_key', label: 'Key (unique)', type: 'text', createOnly: true, required: true, placeholder: 'e.g. respond_in_stock_known' },
      { name: 'rule_text', label: 'Rule text', type: 'textarea', required: true },
      { name: 'hard_rule_type', label: 'Hard rule type (optional)', type: 'select', options: HARD_TYPES },
      { name: 'priority', label: 'Priority (lower = applied first)', type: 'number', default: 100 },
    ],
  },
  segment: {
    label: 'Segment playbook',
    endpoint: 'segment',
    blurb: 'Posture and pricing stance by market segment and/or account type. Set at least one of the two match keys; leave one blank to match any.',
    fields: [
      { name: 'segment_key', label: 'Key (unique)', type: 'text', createOnly: true, required: true, placeholder: 'e.g. faa145_reengage' },
      { name: 'market_segment', label: 'Market segment (match)', type: 'text', placeholder: 'e.g. FAA-145 (blank = any)' },
      { name: 'account_type', label: 'Account type (match)', type: 'text', placeholder: 'e.g. Operator (blank = any)' },
      { name: 'posture', label: 'Posture', type: 'textarea' },
      { name: 'pricing_stance', label: 'Pricing stance', type: 'textarea' },
      { name: 'note_text', label: 'Note', type: 'textarea' },
    ],
  },
  customer: {
    label: 'Customer note',
    endpoint: 'customer',
    blurb: 'Per-account relationship notes. Add "do-not-quote" to sensitivity flags to hard-block an account.',
    fields: [
      { name: 'acctno', label: 'Account #', type: 'text', createOnly: true, required: true },
      { name: 'note_text', label: 'Note', type: 'textarea', required: true },
      { name: 'sensitivity_flags', label: 'Sensitivity flags (comma-sep)', type: 'text', placeholder: 'e.g. slow_pay,strategic,do-not-quote' },
    ],
  },
  advisor: {
    label: 'Advisor rule',
    endpoint: 'advisor',
    blurb: 'Governs the Cockpit advisor — the one-line headline + guidance shown to the rep, synthesized across the opportunity, customer, part, repair history and inventory. Shapes tone, emphasis, and red lines.',
    fields: [
      { name: 'rule_key', label: 'Key (unique)', type: 'text', createOnly: true, required: true, placeholder: 'e.g. lead_with_the_decision' },
      { name: 'rule_text', label: 'Rule text', type: 'textarea', required: true },
      { name: 'priority', label: 'Priority (lower = applied first)', type: 'number', default: 100 },
    ],
  },
  nymeria: {
    label: 'AI Sales Agent policy',
    endpoint: 'nymeria',
    blurb: 'Governs how the AI Sales Agent behaves — tool strategy, risk posture, reasoning depth, when to recommend vs. ask, refusal/safety. Injected into the agent every turn. Distinct from Advisor (the recommendation\'s voice).',
    fields: [
      { name: 'rule_key', label: 'Key (unique)', type: 'text', createOnly: true, required: true, placeholder: 'e.g. see_the_board_first' },
      { name: 'rule_text', label: 'Rule text', type: 'textarea', required: true },
      { name: 'priority', label: 'Priority (lower = applied first)', type: 'number', default: 100 },
    ],
  },
  part: {
    label: 'Part guidance',
    endpoint: 'part',
    blurb: 'Guidance that varies by part type. Match on ATA chapter and/or ABC class; leave one blank to match any. Set at least one.',
    fields: [
      { name: 'part_key', label: 'Key (unique)', type: 'text', createOnly: true, required: true, placeholder: 'e.g. engine_ata72' },
      { name: 'ata_chapter', label: 'ATA chapter (match)', type: 'text', placeholder: "e.g. 72 (blank = any)" },
      { name: 'part_class', label: 'ABC class (match)', type: 'text', placeholder: 'A / B / C / D (blank = any)' },
      { name: 'posture', label: 'Posture', type: 'textarea' },
      { name: 'note_text', label: 'Note', type: 'textarea' },
    ],
  },
};

function TierEditor({ tier }) {
  const schema = SCHEMAS[tier];
  const url = `${config.apiBaseUrl}/api/vesper/guidance/${schema.endpoint}`;
  const { data, isLoading, error, refetch } = useAuthenticatedData(url);
  const { post } = useAuthenticatedPost();
  const { put } = useAuthenticatedPut();

  const [modal, setModal] = useState(null); // { mode, values }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const rows = Array.isArray(data) ? data : [];

  const openCreate = () => {
    const values = {};
    schema.fields.forEach((f) => { values[f.name] = f.default ?? ''; });
    values.active = true;
    setModal({ mode: 'create', values });
  };
  const openEdit = (row) => setModal({ mode: 'edit', values: { ...row, active: row.active === true || row.active === 1 } });

  const setField = (name, val) => setModal((m) => ({ ...m, values: { ...m.values, [name]: val } }));

  const save = async (values) => {
    setSaving(true); setMsg(null);
    try {
      const body = {};
      schema.fields.forEach((f) => { body[toPascal(f.name)] = values[f.name] === '' ? null : values[f.name]; });
      body.Active = values.active !== false;
      if (values.id) await put(`${url}/${values.id}`, body);
      else await post(url, body);
      setModal(null);
      refetch();
    } catch (e) {
      setMsg(e?.response?.data?.message || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    const body = {};
    schema.fields.forEach((f) => { body[toPascal(f.name)] = row[f.name] ?? null; });
    body.Active = !(row.active === true || row.active === 1);
    try { await put(`${url}/${row.id}`, body); refetch(); }
    catch (e) { setMsg(e?.response?.data?.message || e.message); }
  };

  if (isLoading) return <Group justify="center" p="lg"><Loader size="sm" /></Group>;
  if (error) return <Text c="red" p="md">Failed to load {schema.label.toLowerCase()}s.</Text>;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed" maw={780}>{schema.blurb}</Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add {schema.label.toLowerCase()}</Button>
      </Group>
      {msg && <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setMsg(null)}>{msg}</Alert>}

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table fz="xs" striped highlightOnHover miw={900}>
            <Table.Thead>
              <Table.Tr>
                {tier === 'house' && <><Table.Th>Key</Table.Th><Table.Th>Rule</Table.Th><Table.Th>Hard</Table.Th><Table.Th>Prio</Table.Th></>}
                {tier === 'segment' && <><Table.Th>Key</Table.Th><Table.Th>Segment</Table.Th><Table.Th>Type</Table.Th><Table.Th>Posture</Table.Th></>}
                {tier === 'customer' && <><Table.Th>Account</Table.Th><Table.Th>Note</Table.Th><Table.Th>Flags</Table.Th></>}
                {tier === 'advisor' && <><Table.Th>Key</Table.Th><Table.Th>Rule</Table.Th><Table.Th>Prio</Table.Th></>}
                {tier === 'nymeria' && <><Table.Th>Key</Table.Th><Table.Th>Rule</Table.Th><Table.Th>Prio</Table.Th></>}
                {tier === 'part' && <><Table.Th>Key</Table.Th><Table.Th>ATA</Table.Th><Table.Th>Class</Table.Th><Table.Th>Posture</Table.Th></>}
                <Table.Th>Ver</Table.Th><Table.Th>Active</Table.Th><Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} style={{ opacity: (r.active === true || r.active === 1) ? 1 : 0.5 }}>
                  {tier === 'house' && <>
                    <Table.Td><Text fw={600} size="xs">{r.rule_key}</Text></Table.Td>
                    <Table.Td maw={420}><Text size="xs" lineClamp={2}>{r.rule_text}</Text></Table.Td>
                    <Table.Td>{r.hard_rule_type ? <Badge color="red" size="xs" variant="light" leftSection={<IconShieldLock size={10} />}>{r.hard_rule_type}</Badge> : <Text c="dimmed" size="xs">—</Text>}</Table.Td>
                    <Table.Td>{r.priority}</Table.Td>
                  </>}
                  {tier === 'segment' && <>
                    <Table.Td><Text fw={600} size="xs">{r.segment_key}</Text></Table.Td>
                    <Table.Td>{r.market_segment || <Text c="dimmed" size="xs">any</Text>}</Table.Td>
                    <Table.Td>{r.account_type || <Text c="dimmed" size="xs">any</Text>}</Table.Td>
                    <Table.Td maw={360}><Text size="xs" lineClamp={2}>{r.posture}</Text></Table.Td>
                  </>}
                  {tier === 'customer' && <>
                    <Table.Td><Text fw={600} size="xs">{r.acctno}</Text></Table.Td>
                    <Table.Td maw={460}><Text size="xs" lineClamp={2}>{r.note_text}</Text></Table.Td>
                    <Table.Td>{r.sensitivity_flags || <Text c="dimmed" size="xs">—</Text>}</Table.Td>
                  </>}
                  {tier === 'advisor' && <>
                    <Table.Td><Text fw={600} size="xs">{r.rule_key}</Text></Table.Td>
                    <Table.Td maw={520}><Text size="xs" lineClamp={2}>{r.rule_text}</Text></Table.Td>
                    <Table.Td>{r.priority}</Table.Td>
                  </>}
                  {tier === 'nymeria' && <>
                    <Table.Td><Text fw={600} size="xs">{r.rule_key}</Text></Table.Td>
                    <Table.Td maw={520}><Text size="xs" lineClamp={2}>{r.rule_text}</Text></Table.Td>
                    <Table.Td>{r.priority}</Table.Td>
                  </>}
                  {tier === 'part' && <>
                    <Table.Td><Text fw={600} size="xs">{r.part_key}</Text></Table.Td>
                    <Table.Td>{r.ata_chapter || <Text c="dimmed" size="xs">any</Text>}</Table.Td>
                    <Table.Td>{r.part_class || <Text c="dimmed" size="xs">any</Text>}</Table.Td>
                    <Table.Td maw={360}><Text size="xs" lineClamp={2}>{r.posture}</Text></Table.Td>
                  </>}
                  <Table.Td>{r.version}</Table.Td>
                  <Table.Td><Switch size="xs" checked={r.active === true || r.active === 1} onChange={() => toggleActive(r)} /></Table.Td>
                  <Table.Td><Tooltip label="Edit"><ActionIcon variant="subtle" size="sm" onClick={() => openEdit(r)}><IconEdit size={15} /></ActionIcon></Tooltip></Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && <Table.Tr><Table.Td colSpan={7}><Text c="dimmed" ta="center" py="md">None yet.</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <Modal opened={!!modal} onClose={() => setModal(null)} title={modal?.mode === 'create' ? `New ${schema.label.toLowerCase()}` : `Edit ${schema.label.toLowerCase()}`} size="lg">
        {modal && (
          <Stack gap="sm">
            {schema.fields.map((f) => {
              const disabled = f.createOnly && modal.mode === 'edit';
              const v = modal.values[f.name] ?? '';
              if (f.type === 'textarea') return <Textarea key={f.name} label={f.label} value={v} minRows={2} autosize disabled={disabled} required={f.required} placeholder={f.placeholder} onChange={(e) => setField(f.name, e.currentTarget.value)} />;
              if (f.type === 'number') return <NumberInput key={f.name} label={f.label} value={v} disabled={disabled} onChange={(val) => setField(f.name, val)} />;
              if (f.type === 'select') return <Select key={f.name} label={f.label} data={f.options.map((o) => ({ value: o, label: o || '(none — soft guidance)' }))} value={v || ''} disabled={disabled} onChange={(val) => setField(f.name, val)} />;
              return <TextInput key={f.name} label={f.label} value={v} disabled={disabled} required={f.required} placeholder={f.placeholder} onChange={(e) => setField(f.name, e.currentTarget.value)} />;
            })}
            <Switch label="Active" checked={modal.values.active !== false} onChange={(e) => setField('active', e.currentTarget.checked)} />
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" onClick={() => setModal(null)}>Cancel</Button>
              <Button loading={saving} onClick={() => save(modal.values)}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function toPascal(snake) {
  return snake.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

export default function GuidanceAdmin() {
  const [tab, setTab] = useState('house');
  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <div>
        <Title order={2}>Guidance</Title>
        <Text size="sm" c="dimmed">The playbook the sales enrichment pipeline reasons over. Written once here, applied uniformly to every opportunity, and cited on every decision.</Text>
      </div>
      <Group gap="xs">
        {Object.entries(SCHEMAS).map(([k, s]) => (
          <Button key={k} size="xs" variant={tab === k ? 'filled' : 'light'} onClick={() => setTab(k)}>{s.label}s</Button>
        ))}
      </Group>
      <TierEditor key={tab} tier={tab} />
    </Stack>
  );
}
